import { Component, OnInit, Input, Output, EventEmitter, ViewChild, ElementRef, OnChanges, SimpleChanges } from '@angular/core';
import { MatChipEvent, MatChipSelectionChange, MatChipList } from "@angular/material/chips";
import * as lucene from 'lucene';
import { OpenaiService } from "../openai.service";
import { PersonSearchService } from "src/app/_services/person-search.service";
import { map } from "rxjs/operators";
import { Subscription } from 'rxjs';

export interface SuggestedTitle {
  name: string;
  key: string;
  type: 'title' | 'skill';
  selected?: boolean;
  count?: number;
}

@Component({
  selector: 'openai-suggested-titles',
  templateUrl: './suggested-titles.component.html',
  styleUrls: ['./suggested-titles.component.scss']
})
export class SuggestedTitlesComponent implements OnInit, OnChanges {

  private aggreateRequest = {
    aggregatetype: "jobtitle",
    startindex: 0,
    resultsize: 20,
    aggregatecount: 200,
    searchviaboolean: true,
    booleanquery: "*",
    jobtitles: [/** jobtitles */],
    pastjobtitles: [],
    preferredskills: [],
    mandatoryskills: [],
    locations: [],
    continentstobeincluded: ["asia"],
    companies: [],
    industries: [],
    exclusionlist: [],
    genders: ["All"],
    seniority: ["All"],
    graduationyear: [],
    schools: [],
    schooltypes: [],
    educationaldegrees: [],
    educationalmajors: [],
    candidateshavingemails: false,
    candidateshavingcontactnumbers: false,
    minexperience: null,
    maxexperience: null,
    minexperienceinccurrentcompany: null,
    zipcodes: [],
    companysizes: ["All"],
    candidateshavingcontacts: false,
    workregions: [],
    workcountries: [],
    worklocalities: [],
    spokenlanguages: [],
    contacts: ["All"],
    candidateshavingmobilenumber: false,
    candidateshavingpersonalemail: false,
    candidateshavingprofessionalemail: false,
    candidateshavingsocialprofiles: false,
    fromgraduationyears: [],
    tograduationyears: [],
    exclude_jobtitles: [],
    exclude_companies: [],
    exclude_locations: [],
    companytype: "Current",
    jobtitletype: "Current",
    industrytype: "Current",
    fromyearsofexperience: [],
    toyearsofexperience: [],
    experiencetype: "Total",
    likelytomovetype: null,
    salaries: ["All"],
    salaryranges: ["All"],
    functions: []
  }

  @ViewChild('faderRight', { static: true }) faderRight: ElementRef<HTMLDivElement>;
  @ViewChild('faderLeft', { static: true }) faderLeft: ElementRef<HTMLDivElement>;

  @ViewChild('aiChipList', { static: true }) aiChipList: MatChipList;

  @Input('wrap') wrap: boolean = true;

  /**
   * Show only titles or skills or both
   * 'pasttitle' - show only past titles (is standalone and not included in both)
   */
  @Input('showOnly') showOnly: 'pasttitle' | 'titles' | 'skills' | 'both' = 'both';

  @Input('showIcons') showIcons: boolean = true;

  @Input('removeSelected') removeSelected: boolean = false;

  @Input('query') inputQuery: string;
  @Input('reactiveQuery') isReactiveQuery: boolean = true;
  query: string;

  @Input('keepSelected') keepSelected: boolean = true;

  @Input('showCount') showCount: boolean = false;

  @Input('continent') continent: string[] = ['asia'];

  @Input('onlyExisiting') onlyExisiting: boolean = true;

  @Input('exclude') inputExclude: string[] = [];

  @Input('updateChips') updateChips: EventEmitter<SuggestedTitle[]> = null;

  private exclude: string[] = [];

  get excludeTerms(): string[] {
    return [].concat(this.exclude, this.inputExclude).map(t => t.trim().toLowerCase());
  }

  @Input('selectedTitles') selectedTitles: SuggestedTitle[] = [];
  @Output('change') change: EventEmitter<SuggestedTitle[]> = new EventEmitter<SuggestedTitle[]>();

  @Output('queryChange') queryChange: EventEmitter<string> = new EventEmitter<string>();

  @Output('loading') loading: EventEmitter<{ state: boolean, count?: number }> = new EventEmitter<{ state: boolean, count?: number }>();

  updateChipSubscription: Subscription;

  get queryTerms(): string[] {
    return this.booleanQueryParser(this.query);
  }

  get queryTermsString(): string {
    // rebuild the query string
    const selectedTitles = this.suggested.filter(t => t.selected).map(t => t.name);
    const ast = lucene.parse(`${this.query}`) as lucene.BinaryAST;
    let lastAst = ast;
    // add all selected titles
    selectedTitles.forEach((t, i) => {
      let prevRight: lucene.BinaryAST;
      prevRight = lastAst.right as lucene.BinaryAST;
      if (prevRight) {
        lastAst.right = {
          // @ts-ignore
          right: { left: { term: t, quoted: t.includes(' ') } },
          operator: 'OR',
          left: prevRight
        };
      } else {
        lastAst.right = {
          // @ts-ignore
          left: { term: t, quoted: t.includes(' ') },
        };
      }
      if (!lastAst.operator || lastAst.operator === "<implicit>") {
        lastAst.operator = 'OR';
      }
      lastAst = lastAst.right as lucene.BinaryAST;
    });
    return lucene.toString(ast);
  }

  suggested: SuggestedTitle[] = [];

  constructor(private openaiService: OpenaiService, private personSerachService: PersonSearchService) { }

  private removeExcludedTitles() {
    this.suggested = this.suggested.filter(s => !this.excludeTerms.includes(s.name.toLowerCase()));
  }


  private autoSelectTitles(suggested: SuggestedTitle[]) {
    if (this.keepSelected) {
      // add selected status to the titles
      const titleToAppend = [];
      this.selectedTitles.forEach(selectedTitle => {
        const index = suggested.findIndex(t => t.key === selectedTitle.key);
        if (index >= 0) {
          suggested[index].selected = true;
        } else {
          titleToAppend.push(selectedTitle);
        }
      });
      suggested.push(...titleToAppend);
    }
    if (this.removeSelected) {
      // remove all selected titles from the list
      suggested = suggested.filter(t => !t.selected);
    }
    return suggested;
  }

  private booleanQueryParser(query: string): string[] {
    const parsedAst = lucene.parse(query);
    const terms = [];
    // collect all terms
    const collectTerms = (ast: any) => {
      if ( ast.left) {
        collectTerms(ast.left);
      }
      if (ast.right) {
        collectTerms(ast.right);
      }
      if (ast.term) {
        terms.push(ast.term);
      }
    };
    collectTerms(parsedAst);
    return terms;
  }

  private getSafeKey(str: string): string {
    return str.toLowerCase().replace(/[^a-zA-Z0-9]/g, '');
  }

  ngOnInit() {
    const faderRightElement = this.faderRight.nativeElement;
    const faderLeftElement = this.faderLeft.nativeElement;
    // @ts-ignore
    if (this.aiChipList && this.aiChipList._elementRef && this.aiChipList._elementRef.nativeElement) {
      // @ts-ignore
      const chipListElement = this.aiChipList._elementRef.nativeElement;
      // attach scroll event
      chipListElement.addEventListener('scroll', () => {
        const scrollOffset = chipListElement.scrollWidth - chipListElement.clientWidth;
        if (chipListElement.scrollLeft > scrollOffset - 5) {
          faderRightElement.style.visibility = 'hidden';
        } else {
          faderRightElement.style.visibility = 'visible';
        }

        if (chipListElement.scrollLeft === 0) {
          faderLeftElement.style.visibility = 'hidden';
        } else {
          faderLeftElement.style.visibility = 'visible';
        }
      });
    }
    this.query = this.inputQuery ? this.inputQuery.trim() : "";
    if (!this.query) {
      return this.loading.emit({ state: false, count: 0 });
    }
    
    this.populateSuggestions(faderRightElement, faderLeftElement);
  }

  ngOnChanges(change: SimpleChanges) {
    if (change.inputQuery && !change.inputQuery.firstChange && this.isReactiveQuery) {
      this.query = this.inputQuery ? this.inputQuery.trim() : "";
      if (!this.query) {
        this.suggested = [];
        return this.loading.emit({ state: false, count: 0 });
      }
      this.populateSuggestions(this.faderRight.nativeElement, this.faderLeft.nativeElement);
    }
  }

  private populateSuggestions(faderRightElement: HTMLElement, faderLeftElement: HTMLElement) {
    if (this.updateChips && !this.removeSelected) {
      this.updateChips.subscribe((titles: SuggestedTitle[]) => {
        titles.forEach(title => {
          const index = this.suggested.findIndex(t => t.key === title.key);
          if (index >= 0) {
            this.suggested[index].selected = !!title.selected;
            this.selectedTitles = this.suggested.filter(t => t.selected);
          }
        });
        this.updateChipProgrammatically(titles)
      });
    }
    this.suggested = [];
    let holdSuggested = [];
    faderRightElement.style.visibility = 'hidden';
    faderLeftElement.style.visibility = 'hidden';
    this.loading.emit({state: true});
    this.openaiService.getUnifiedData(encodeURIComponent(this.query)).subscribe(async response => {

      const [titles, skills] = (
        Array.isArray(response) ?
        response.reduce(
          (a, b) => [a[0].concat(b.similar ? b.similar.titles : []), a[1].concat(b.similar ? b.similar.skills : [])],
          [[], []]
        ) : [response.similar ? (response.similar.titles || []) : [], response.similar ? (response.similar.skills || []) : []]).map(a => a.filter(Boolean));

      if (this.showOnly === 'titles' || this.showOnly === 'pasttitle') {
        skills.length = 0;
      }

      if (this.showOnly === 'skills') {
        titles.length = 0;
      }

      if (this.onlyExisiting) {
        const aiTitles = titles.map(s => s.trim());
        const aggreateRequestTitle = Object.assign({}, this.aggreateRequest);
        aggreateRequestTitle.aggregatetype = "jobtitle";
        if (this.showOnly === 'pasttitle') {
          aggreateRequestTitle.pastjobtitles = aiTitles;
        } else {
          aggreateRequestTitle.jobtitles = aiTitles;
        }
        aggreateRequestTitle.continentstobeincluded = this.continent;

        const aiSkills = skills.map(s => s.trim());
        const aggreateRequestSkill = Object.assign({}, this.aggreateRequest);
        aggreateRequestSkill.aggregatetype = "skills";
        aggreateRequestSkill.preferredskills = aiSkills;
        aggreateRequestSkill.continentstobeincluded = this.continent;

        const titleAggragateP = (this.showOnly === 'titles' || this.showOnly === 'pasttitle' || this.showOnly === 'both') && aiTitles.length ? this.personSerachService.getTotalAggs(aggreateRequestTitle).pipe(map(res => res.data.aggregations.jobtitle)).toPromise() : Promise.resolve([]);
        const skillAggragateP = (this.showOnly === 'skills' || this.showOnly === 'both') && aiSkills.length ? this.personSerachService.getTotalAggs(aggreateRequestSkill).pipe(map(res => res.data.aggregations.skills)).toPromise() : Promise.resolve([]);

        const [titleAggragate, skillAggragate] = await Promise.all([titleAggragateP, skillAggragateP]);


        holdSuggested = [].concat(
          titles.map(title => {
            const count = titleAggragate.find(r => r.key.toLowerCase() === title.toLowerCase())?.doc_count || 0;
            return {
              name: title,
              key: this.getSafeKey(title),
              count: count,
              type: 'title'
            };
          }),
          skills.map(skill => {
            const count = skillAggragate.find(r => r.key.toLowerCase() === skill.toLowerCase())?.doc_count || 0;
            return {
              name: skill,
              key: this.getSafeKey(skill),
              count: count,
              type: 'skill'
            };
          })
        ).sort((a, b) => b.count - a.count).filter(h => h.count);
      } else {
        holdSuggested = [].concat(
          titles.map(title => ({ name: title, key: this.getSafeKey(title), type: 'title' })),
          skills.map(skill => ({ name: skill, key: this.getSafeKey(skill), type: 'skill' }))
        );
      }

      // remove duplicate
      const keys = [];
      holdSuggested = holdSuggested.filter(t => {
        if (keys.includes(t.key)) {
          return false;
        }
        keys.push(t.key);
        return true;
      });
      this.suggested = this.autoSelectTitles(holdSuggested);
      this.updateSelectedTitles(false);

      setTimeout(() => {
        // programmatically select the chip
        this.updateChipProgrammatically(this.selectedTitles);
        if (!this.aiChipList.chips || !this.aiChipList.chips.last) {
          return;
        }
        const lastChipElement = this.aiChipList.chips.last._elementRef.nativeElement;
        const parentElement = lastChipElement.offsetParent as HTMLElement;
        // check if the last chip is visible on the screen
        if (lastChipElement.offsetLeft + lastChipElement.clientWidth > parentElement.clientWidth) {
          faderRightElement.style.visibility = 'visible';
        } else {
          faderRightElement.style.visibility = 'hidden';
        }
      });
      this.loading.emit({state: false, count: this.suggested.length });
    });
  }

  public scrollChipList(direction: string) {
    const chipListElement = this.aiChipList.chips.first._elementRef.nativeElement.offsetParent as HTMLElement;
    const scrollAmount = chipListElement.clientWidth / 2;
    if (direction === 'left') {
      chipListElement.scrollTo({ left: chipListElement.scrollLeft - scrollAmount, behavior: 'smooth' });
    } else {
      chipListElement.scrollTo({ left: chipListElement.scrollLeft + scrollAmount, behavior: 'smooth' });
    }
  }

  private updateSelectedTitles(emit: boolean = true) {
    this.removeExcludedTitles();
    this.selectedTitles = this.suggested
      .filter(t => t.selected);
    if (emit) {
      this.change.emit(this.selectedTitles);
      this.queryChange.emit(this.queryTermsString);
    }
  }

  removeTitle(event: MatChipEvent) {
    const chip = event.chip;
    const title: SuggestedTitle = chip.value;
    if (title) {
      const index = this.suggested.findIndex(t => t.key === title.key);
      if (index >= 0) {
        this.suggested.splice(index, 1);
      }
    }
    this.updateSelectedTitles();
  }

  updateSelectionStatus(event: MatChipSelectionChange) {
    const chip = event.isUserInput ? event.source : null;
    if (!chip) {
      return;
    }

    const title: SuggestedTitle = chip.value;
    if (title) {
      const index = this.suggested.findIndex(t => t.key === title.key);
      if (index >= 0) {
        this.suggested[index].selected = event.selected;
        this.updateSelectedTitles();
        if (event.selected && this.removeSelected) {
          this.suggested.splice(index, 1);
          this.loading.emit({state: false, count: this.suggested.length });
        }
      }
    }

  }

  formatNumberToIndianFormat(number) {
    if (number) {
      const suffixes = ['', 'K+', 'M+', 'B+', 'T+']; // Add more suffixes as needed for larger numbers
      for (const suffix of suffixes) {
        if (Math.abs(number) < 1000) {
          return Math.floor(number) + suffix;
        }
        number /= 1000;
      }
      return Math.floor(number) + 'T';
    } else {
      return 0
    }
  }

  private updateChipProgrammatically(titles: SuggestedTitle[]) {
    this.aiChipList.chips.forEach(chip => {
      const index = titles.findIndex(t => t.key === chip.value.key);
      if (index >= 0) {
        const isSelected = titles[index].selected;
        if (isSelected && !chip.selected) {
          chip.select();
        } else if (!isSelected && chip.selected) {
          chip.deselect();
        }
      }
    });
  }

}
